import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Megaphone, TrendingUp, Eye, Target, Package, Store, FileImage,
  ChevronRight, Zap, Shield, BarChart3, DollarSign, Users,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const FEATURES = [
  { icon: Target, title: 'Segmentacion', desc: 'Llega exactamente a tu publico objetivo por categoria y ubicacion.' },
  { icon: BarChart3, title: 'Metricas en tiempo real', desc: 'Impresiones, clicks y conversion. Todo medible, todo optimizable.' },
  { icon: DollarSign, title: 'Tu decides cuanto invertir', desc: 'Desde $100 MXN. Presupuesto diario o total, tu controlas el gasto.' },
  { icon: Shield, title: 'Aprobacion humana', desc: 'Cada anuncio es revisado por nuestro equipo para garantizar calidad.' },
];

const AD_TYPES = [
  { icon: Package, title: 'Producto', desc: 'Destaca un producto especifico en el feed de compradores.', color: 'from-orange-500 to-amber-500' },
  { icon: Store, title: 'Tu Tienda', desc: 'Promociona tu perfil de vendedor y aumenta tus seguidores.', color: 'from-blue-500 to-cyan-500' },
  { icon: FileImage, title: 'Banner', desc: 'Sube tu propia imagen promocional con enlace personalizado.', color: 'from-purple-500 to-fuchsia-500' },
];

const AdsLanding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleCTA = () => {
    navigate(user ? '/advertiser/campaigns' : '/login');
  };

  return (
    <div className="min-h-screen bg-white -mt-4">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 pt-16 pb-20 sm:pt-24 sm:pb-28">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-[120px]" />
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-amber-500 rounded-full blur-[150px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-1.5 mb-6">
            <Megaphone className="w-4 h-4 text-orange-400" />
            <span className="text-sm text-orange-300 font-medium">Zona Libre Ads</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
            Impulsa tus ventas<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">al siguiente nivel</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-10">
            Llega a miles de compradores activos. Elige que promocionar, define tu presupuesto y mide tus resultados en tiempo real.
          </p>
          <Button onClick={handleCTA} size="lg"
            className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-lg px-10 py-4 rounded-full shadow-xl shadow-orange-500/20"
            data-testid="ads-cta-hero">
            <Zap className="w-5 h-5 mr-2" /> Crear mi Anuncio
          </Button>
          <p className="text-xs text-gray-500 mt-3">Desde $100 MXN · Sin contratos · Cancela cuando quieras</p>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-gray-50 py-6 border-b">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl sm:text-3xl font-black text-gray-900">10K+</p>
            <p className="text-xs sm:text-sm text-gray-500">Compradores activos</p>
          </div>
          <div>
            <p className="text-2xl sm:text-3xl font-black text-orange-500">3x</p>
            <p className="text-xs sm:text-sm text-gray-500">Mas visibilidad</p>
          </div>
          <div>
            <p className="text-2xl sm:text-3xl font-black text-gray-900">$0.50</p>
            <p className="text-xs sm:text-sm text-gray-500">CPC promedio</p>
          </div>
        </div>
      </section>

      {/* Ad Types */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">Elige que promocionar</h2>
          <p className="text-gray-500 text-center mb-10 sm:mb-14">Tres formatos diseñados para diferentes objetivos</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {AD_TYPES.map((t, i) => {
              const Icon = t.icon;
              return (
                <Card key={i} className="relative overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 cursor-pointer group" onClick={handleCTA}>
                  <div className={`h-2 bg-gradient-to-r ${t.color}`} />
                  <div className="p-6 text-center">
                    <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${t.color} flex items-center justify-center`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-bold mb-2">{t.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{t.desc}</p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-gray-50 py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12">Por que anunciarte aqui</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">{f.title}</h3>
                    <p className="text-gray-500 text-sm">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 sm:py-20 text-center">
        <div className="max-w-2xl mx-auto px-4">
          <Users className="w-12 h-12 mx-auto text-orange-500 mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Listo para empezar?</h2>
          <p className="text-gray-500 mb-8">Crea tu primera campana en menos de 2 minutos.</p>
          <Button onClick={handleCTA} size="lg"
            className="bg-orange-500 hover:bg-orange-600 text-white text-lg px-10 py-4 rounded-full"
            data-testid="ads-cta-bottom">
            Crear Anuncio <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AdsLanding;
